import React, { useState, useEffect } from 'react';
import Slider from 'react-input-slider';
import Rodal from 'rodal';
import ReactTooltip from 'react-tooltip';

import { setResults, getSliderDefaultStyles } from './utils';
import { getAuthTokenFromStorage, capitalizeEachWord } from '../../utils';

import './style.scss';

const sliderDefaultStyles = getSliderDefaultStyles();

const questionIcon = require('../../assets/icons/question-icon.svg');
const redExclamationIcon = require('../../assets/icons/red-exclamation.svg');

const QuizScreen = (props) => {
  const {
    createFlaggedTopic,
    fetchAllTopicsForRace,
    fetchRace,
    flaggedTopicsForRace,
    history,
    match: {
      params: {
        id: quizId,
      } = {},
    } = {},
    nationalTopics,
    selectedRace,
    setNavReferrer,
    setRaceTopicMeaning,
    setUserQuizScores,
    user,
  } = props;

  const nationalTopicFields = nationalTopics.map(t => t.title);

  const raceQuizTopics = Object.keys(selectedRace?.topic_stances?.scores || {})
    .filter(topic => !nationalTopicFields.includes(topic));

  const {
    race_stances: {
      [quizId]: userRaceStances,
    } = {},
    national_stances: userNationalStances = {},
  } = user || {};

  const [quizTopics, setQuizTopics] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});

  const [modalVisible, setModalVisible] = useState(false);
  const [quizIncludesNational, setQuizIncludesNational] = useState(false);
  const [quizIncludesRace, setQuizIncludesRace] = useState(false);
  const [isAuthed, setIsAuthed] = useState(true);

  const [confusedModalVisible, setConfusedModalVisible] = useState(false);
  const [flaggedModalVisible, setFlaggedModalVisible] = useState(false);
  const [currFlaggedTopic, setCurrFlaggedTopic] = useState();
  const [currFlaggedTopicReason, setCurrFlaggedTopicReason] = useState('');
  const [currFlaggedTopicMeaning, setCurrFlaggedTopicMeaning] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const tokenExists = !!getAuthTokenFromStorage();
    setIsAuthed(tokenExists);
    if (!tokenExists) setModalVisible(true);
  }, []);

  useEffect(() => {
    if (quizId) fetchRace(quizId);
  }, [quizId]);

  useEffect(() => {
    let topics = [];

    // check if race is specified for quiz
    if (quizId && selectedRace && selectedRace.topic_stances) {
      // if user has never taken this race quiz before, include those topics
      if (!userRaceStances) {
        topics = [...raceQuizTopics];
        setQuizIncludesRace(true);
      } else {
        // user has taken race quiz before -- ask if want to take again
        setModalVisible(true);
      }
    }

    // if user has never taken the national quiz before, include those topics
    if (Object.keys(userNationalStances).length === 0) {
      topics = [...nationalTopicFields, ...topics];
      setQuizIncludesNational(true);
    } else if (topics.length === 0) {
      // user has taken national quiz before -- ask if want to take again (only if no race quiz to be taken)
      setModalVisible(true);
    }

    setQuizTopics(topics);
  }, [user, nationalTopics]);

  // set default answers to be 0 when the set of quiz topics changes
  useEffect(() => {
    const initAnswers = quizTopics.reduce((acc, topic) => ({
      ...acc,
      [topic]: 0,
    }), {});

    setUserAnswers(initAnswers);
  }, [quizTopics]);

  useEffect(() => {
    if (!flaggedTopicsForRace && quizId) {
      fetchAllTopicsForRace(quizId);
    }
  }, [flaggedTopicsForRace, quizId]);

  const retakeQuiz = () => {
    setQuizTopics([...nationalTopicFields, ...raceQuizTopics]);
    setModalVisible(false);
    setQuizIncludesNational(true);
    setQuizIncludesRace(raceQuizTopics.length > 0);
  };

  const viewResults = () => {
    if ((quizIncludesRace || raceQuizTopics.length > 0) && quizId) {
      history.push(`/compare-candidates/${quizId}`);
    } else if (quizIncludesNational || !quizId) {
      history.push('/quiz/results');
    }
  };

  const redirectToAuth = () => {
    setModalVisible(false);
    history.push('/sign-up');
  };

  const setAnswer = (topic, value) => {
    setUserAnswers(answers => ({
      ...answers,
      [topic]: parseFloat(value.toFixed(2)),
    }));
  };

  const natlQuizTopicsFields = nationalTopics;
  const raceQuizTopicFields = quizTopics.filter(topic => !nationalTopicFields.includes(topic));

  const renderSlider = title => (
    <Slider
      axis="x"
      data-tip="Drag to the left or the right based on how you stand"
      xstep={0.01}
      xmin={-1}
      xmax={1}
      x={userAnswers[title] || 0}
      onChange={({ x }) => setAnswer(title, x)}
      styles={sliderDefaultStyles}
    />
  );

  const routeToAuth = (path) => {
    setNavReferrer(history.location.pathname);
    history.push(path);
  };

  const submitFlaggedTopic = () => {
    createFlaggedTopic({ topic: currFlaggedTopic, reason: currFlaggedTopicReason, race: quizId });
    setConfusedModalVisible(false);
    setCurrFlaggedTopicReason('');
    setCurrFlaggedTopic();
  };

  const submitFlaggedTopicMeaning = () => {
    setRaceTopicMeaning(quizId, currFlaggedTopic, currFlaggedTopicMeaning);
    setConfusedModalVisible(false);
    setCurrFlaggedTopicMeaning('');
    setCurrFlaggedTopic();
  };

  const flaggedTopics = flaggedTopicsForRace ? flaggedTopicsForRace.map(topic => topic.topic) : [];

  const topicMeanings = selectedRace?.topic_meanings || [];

  return (
    <div id="quiz-content">
      <div id="info-header">
        <h1>Your Customized Candidate-Finding Quiz</h1>
        <div id="description-container">
          <p>We rank each candidate by the following criteria based on publicly available speech and website data.</p>
          <p>Drag the buttons below to the left or the right according to how you stand, on a scale of strongly against to strongly in favor.</p>
        </div>
      </div>

      {natlQuizTopicsFields.length > 0 ? (
        <div className="rank-containers">
          <div className="candidate-natl-topic-container content-container">
            <div className="top-info-container">
              <h2>Stance on National Topics</h2>
              <p>We estimate candidate stance on these topics of national interest for every candidate in our database.</p>
            </div>
            <div className="candidate-stance-table-container">
              <div className="favor-score-header-container">
                <p>Strongly Against</p>
                <p>Against</p>
                <p>Neutral</p>
                <p>In Favor</p>
                <p>Strongly In Favor</p>
              </div>
              <table>
                {natlQuizTopicsFields.map(({ title, description }, idx) => (
                  <tr key={title}>
                    <td className="topic-stance" style={idx === natlQuizTopicsFields.length - 1 ? { borderBottom: 0 } : {}}>{capitalizeEachWord(description)}</td>
                    <td className="topic-value" style={idx === natlQuizTopicsFields.length - 1 ? { borderBottom: 0 } : {}}>
                      <div className="preview-parent">
                        <div className="candidate-preview-container">
                          {renderSlider(title)}
                          <ReactTooltip />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {raceQuizTopicFields.length > 0 ? (
        <div className="rank-containers">
          <div className="candidate-natl-topic-container content-container">
            <div className="top-info-container">
              <h2>Stance on Topics for {selectedRace?.title}</h2>
              <p>We run a machine learning algorithm to determine topics/issues relevant to this specific race. We’re working on making the topic names more
                intuitive, but for now you may have to infer the meaning.
              </p>
            </div>
            <div className="candidate-stance-table-container">
              <div className="favor-score-header-container">
                <p>Strongly Against</p>
                <p>Against</p>
                <p>Neutral</p>
                <p>In Favor</p>
                <p>Strongly In Favor</p>
              </div>
              <table>
                {raceQuizTopicFields.map((title, idx) => (
                  <tr key={title}>
                    <td className="topic-stance" style={idx === raceQuizTopicFields.length - 1 ? { borderBottom: 0 } : {}}>
                      {capitalizeEachWord(topicMeanings[title] || title || '')}
                      <img
                        src={questionIcon}
                        alt="question icon"
                        className="question-icon"
                        onClick={() => {
                          setConfusedModalVisible(true);
                          setCurrFlaggedTopic(title);
                        }}
                      />
                      {flaggedTopics.includes(title)
                        ? <img src={redExclamationIcon} alt="question icon" className="question-icon" onClick={() => { setFlaggedModalVisible(true); setCurrFlaggedTopic(title); }} />
                        : null}
                    </td>
                    <td className="topic-value" style={idx === raceQuizTopicFields.length - 1 ? { borderBottom: 0 } : {}}>
                      <div className="preview-parent">
                        <div className="candidate-preview-container">
                          {renderSlider(title)}
                          <ReactTooltip />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {quizTopics.length > 0 ? (
        <div id="submit-container">
          {isLoading || errorMessage ? (
            <h3 id="loading-text">{errorMessage || 'Loading...'}</h3>
          ) : (
            <button
              type="button"
              onClick={() => {
                setIsLoading(true);

                setResults(userAnswers, nationalTopicFields, quizId, setUserQuizScores, () => {
                  setIsLoading(false);
                  viewResults();
                }, () => {
                  setIsLoading(false);
                  setErrorMessage('An error occurred. Please try again.');
                });
              }}
            >Submit Answers
            </button>
          )}
        </div>
      ) : null}

      <Rodal visible={modalVisible} onClose={isAuthed ? retakeQuiz : redirectToAuth} height={150}>
        {isAuthed ? (
          <div className="modal-quiz-content">
            <h3>It looks like you&apos;ve taken part of this quiz before.</h3>
            <div id="button-container">
              <button type="button" onClick={retakeQuiz}>Retake Quiz</button>
              <button type="button" onClick={viewResults}>View Results</button>
            </div>
          </div>
        ) : (
          <div className="modal-quiz-content">
            <h3>You&apos;re not logged in.</h3>
            <p>You must be logged in to take and view quiz results.</p>
            <div id="button-container">
              <button type="button" onClick={() => routeToAuth('/sign-up')}>Sign Up</button>
              <button type="button" onClick={() => routeToAuth('/login')}>Login</button>
            </div>
          </div>
        )}
      </Rodal>

      <Rodal
        visible={confusedModalVisible}
        onClose={() => {
          setConfusedModalVisible(false);
          setCurrFlaggedTopic();
          setCurrFlaggedTopicReason('');
        }}
        height={475}
      >
        <div className="submit-flag-topic-modal">
          <h2>Confused?</h2>
          <p>These topics were extracted by our machine learning algorithm, which might be wrong.</p>
          <p>You can either provide an interpretation of what this topic might mean, or flag this topic as irrelevant to the race.</p>
          <p>Once you submit, our moderators will review your request.</p>
          <br />
          <h4>Original topic phrase: {capitalizeEachWord(currFlaggedTopic || '')}</h4>
          <div className="flag-topic-input-container">
            <h4>Submit a better meaning</h4>
            <input
              placeholder="Interpretation of the topic"
              type="text"
              value={currFlaggedTopicMeaning}
              onChange={event => setCurrFlaggedTopicMeaning(event.target.value)}
              className="flagged-link-reason"
            />
            <button type="button" onClick={submitFlaggedTopicMeaning} className="flag-topic-button" id="submit-flagged-topic-meaning">Submit meaning</button>
          </div>
          <div className="flag-topic-input-container">
            <h4>Tell us why you are confused</h4>
            <input
              placeholder="reason..."
              type="text"
              value={currFlaggedTopicReason}
              onChange={event => setCurrFlaggedTopicReason(event.target.value)}
              className="flagged-link-reason"
            />
            <button type="button" onClick={submitFlaggedTopic} className="flag-topic-button" id="submit-flagged-topic-reason">Flag this topic for review</button>
          </div>
        </div>
      </Rodal>

      {flaggedTopicsForRace
        ? (
          <Rodal
            visible={flaggedModalVisible}
            onClose={() => {
              setFlaggedModalVisible(false);
              setCurrFlaggedTopic();
            }}
            width={450}
          >
            <div className="flagged-topic-modal">
              <h2>Flagged Topic</h2>
              <p>This topic has already been flagged as irrelevant or confusing to this race.</p>
              <p>The machine learning algorithm will re-run the data after our moderators review this topic.</p>
              <div>
                Some reasons previous users flagged this topic:
                <ul className="flagged-reason-list">
                  {flaggedTopicsForRace.filter(e => e.topic === currFlaggedTopic).slice(0, 4).map((flaggedTopic) => {
                    return (<li>{`"${flaggedTopic.reason}"`}</li>);
                  })}
                </ul>
              </div>
            </div>
          </Rodal>
        )
        : null
      }
    </div>
  );
};

export default QuizScreen;
